import { useState, useEffect, useCallback } from "react";
import DashboardPage from "./Dashboard.js";
import Sidebar from "./SideBar";
import Header from "./Header";
import OrdersPage from "./Orders";
import OrderPage from "./OrderPage";
import CollectionPage from "./Collection";
import ReportingPage from "./Reports";
import DeliveriesPage from "./Delivery";
import AccountDetailPage from "./Accountdetails";
import AccountSettingsPage from "./Accountsettings";
import CreateOrderPage from "./CreateOrder";
import { Toasts, showToast } from "./SharedUI";
import Login from "./Login.js";
import Register from "./Register.js";
import TwoFactorPage from "./2factor.js";
import MaintenancePage from "./Maintenance.js";
import Inventory from "./Inventory.js";
import Branches from "./Branch.js";
import Warehouses from "./warehouse.js";
import { usePermissions } from "./PermissionsContext";
import Inventory1 from "./Inventory1.js";

function usePageHistory(initialPage = "dashboard") {
  const [stack, setStack] = useState([initialPage]);

  const page = stack[stack.length - 1];
  const canGoBack = stack.length > 1;

  const navigateTo = useCallback((newPage) => {
    setStack((prev) =>
      prev[prev.length - 1] === newPage ? prev : [...prev, newPage],
    );
  }, []);

  const goBack = useCallback(() => {
    setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);

  const resetTo = useCallback((newPage) => {
    setStack([newPage]);
  }, []);

  return { page, navigateTo, goBack, canGoBack, resetTo };
}

export default function App() {
  const { page, navigateTo, goBack, canGoBack, resetTo } =
    usePageHistory("dashboard");

  const [notifs, setNotifs] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedAccount] = useState(null);
  const [theme, setTheme] = useState("light");
  const permissions = usePermissions();

  const [authState, setAuthState] = useState(() => {
    if (window.location.pathname === "/register") return "register";
    return sessionStorage.getItem("isLoggedIn") === "true" ? "app" : "login";
  });

  const [pendingEmail, setPendingEmail] = useState("");

  useEffect(() => {
    if (theme === "light")
      document.documentElement.classList.add("light-theme");
    else document.documentElement.classList.remove("light-theme");
  }, [theme]);

  useEffect(() => {
    if (authState === "app" && permissions && !permissions.canSeePage(page)) {
      resetTo("dashboard");
    }
  }, [page, permissions, authState, resetTo]);

  const handleLogin = useCallback(
    (userObject) => {
      permissions?.reload?.();
      setAuthState("app");
      resetTo("dashboard");
    },
    [permissions, resetTo],
  );

  const handlePendingVerification = useCallback((email) => {
    setPendingEmail(email);
    setAuthState("2fa");
  }, []);

  const handleVerified = useCallback(() => {
    setAuthState("login");
    setPendingEmail("");
    showToast("Account verified! Please sign in.", "success");
  }, []);

  const handleLogout = useCallback(() => {
    sessionStorage.clear();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    permissions?.reload?.();
    setAuthState("login");
    setPendingEmail("");
    resetTo("dashboard");
  }, [permissions, resetTo]);

  const handleNotifRead = useCallback((id) => {
    setNotifs((p) => p.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  if (authState === "register") {
    return (
      <Register
        onRegistered={() => {
          window.history.replaceState({}, "", "/");
          setAuthState("login");
          showToast("Account created! Please sign in.", "success");
        }}
      />
    );
  }
  if (authState === "login") {
    return (
      <Login
        onLogin={handleLogin}
        onPendingVerification={handlePendingVerification}
      />
    );
  }
  if (authState === "2fa") {
    return (
      <TwoFactorPage
        email={pendingEmail}
        onVerified={handleVerified}
        onBack={() => setAuthState("login")}
      />
    );
  }

  const pages = {
    dashboard: (
      <DashboardPage showToast={showToast} permissions={permissions} />
    ),
    Maintenance: (
      <MaintenancePage showToast={showToast} permissions={permissions} />
    ),
    orders: (
      <OrdersPage
        showToast={showToast}
        permissions={permissions}
        onSelectOrder={(o) => {
          setSelectedOrder(o);
          navigateTo("order");
        }}
        setPage={navigateTo}
      />
    ),
    collection: (
      <CollectionPage showToast={showToast} permissions={permissions} />
    ),
    reporting: (
      <ReportingPage showToast={showToast} permissions={permissions} />
    ),
    deliveries: (
      <DeliveriesPage showToast={showToast} permissions={permissions} />
    ),
    "Inventory 1": (
      <Inventory1 showToast={showToast} permissions={permissions} />
    ),
    Inventory: <Inventory showToast={showToast} permissions={permissions} />,
    Branch: <Branches showToast={showToast} permissions={permissions} />,
    warehouses: <Warehouses showToast={showToast} permissions={permissions} />,
    "account-detail": (
      <AccountDetailPage
        account={selectedAccount}
        showToast={showToast}
        permissions={permissions}
        onEdit={() => navigateTo("account-settings")}
      />
    ),
    "account-settings": (
      <AccountSettingsPage showToast={showToast} permissions={permissions} />
    ),
    order: (
      <OrderPage
        order={selectedOrder}
        setPage={navigateTo}
        showToast={showToast}
        permissions={permissions}
      />
    ),
    "create-order": (
      <CreateOrderPage
        setPage={navigateTo}
        showToast={showToast}
        permissions={permissions}
      />
    ),
  };

  const effectivePage = permissions?.canSeePage(page) ? page : "dashboard";

  return (
    <div className="shell">
      {/* ↓ Sidebar uses resetTo — clears stack on every sidebar click */}
      <Sidebar
        page={effectivePage}
        setPage={resetTo}
        permissions={permissions}
      />
      <div className="main">
        <Header
          page={effectivePage}
          notifs={notifs}
          onNotifRead={handleNotifRead}
          setPage={navigateTo}
          goBack={goBack}
          canGoBack={canGoBack}
          theme={theme}
          setTheme={setTheme}
          onLogout={handleLogout}
        />
        {pages[effectivePage] || <DashboardPage />}
      </div>
      <Toasts />
    </div>
  );
}
